// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - STATS MANAGER
// Handles protocol-wide statistics, leaderboards, and personal performance
// ═══════════════════════════════════════════════════════════════════════════

import { getHistory } from './historyManager';

// ═══════════════════════════════════════════════════════════════════════════
// DEMO DATA - Protocol-wide stats (simulated)
// In production, this would come from blockchain indexer or backend API
// ═══════════════════════════════════════════════════════════════════════════

const DEMO_PROTOCOL_STATS = {
  totalPurifications: 15234,
  totalObjectsCleaned: 48921,
  totalPurityEarned: 489210,
  totalSuiReclaimed: 2445.67,
  activePurifiers: 1247,
  categoriesBreakdown: {
    TOXIC: 8934,
    SPAM: 32145,
    DUST: 7842,
  },
  dailyActivity: generateDailyActivity(),
  topPurifiers: generateTopPurifiers(),
};

// Generate demo daily activity (last 30 days)
function generateDailyActivity() {
  const activity = [];
  const now = Date.now();
  const oneDay = 24 * 60 * 60 * 1000;
  
  for (let i = 29; i >= 0; i--) {
    const date = new Date(now - (i * oneDay));
    activity.push({
      date: date.toISOString().split('T')[0],
      purifications: Math.floor(Math.random() * 200) + 300,
      objectsCleaned: Math.floor(Math.random() * 800) + 1200,
      purityEarned: Math.floor(Math.random() * 8000) + 12000,
    });
  }
  
  return activity;
}

// Generate demo top purifiers leaderboard
function generateTopPurifiers() {
  const names = [
    'PurityMaster', 'CleanSweep', 'ToxicSlayer', 'SpamHunter', 'DustCollector',
    'ChainJanitor', 'WalletWizard', 'ScamBuster', 'PureHeart', 'CleanFreak',
  ];
  
  return names.map((name, index) => ({
    rank: index + 1,
    address: `0x${Math.random().toString(16).substr(2, 8)}...${Math.random().toString(16).substr(2, 4)}`,
    displayName: name,
    totalPurity: Math.floor(45000 - (index * 4000) + Math.random() * 1000),
    purifications: Math.floor(1500 - (index * 120) + Math.random() * 50),
    tier: Math.max(0, 6 - Math.floor(index / 2)),
    streak: Math.floor(Math.random() * 30) + 1,
  }));
}

// ═══════════════════════════════════════════════════════════════════════════
// PERSONAL STATS CALCULATION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get user's personal statistics
 */
export function getPersonalStats(walletAddress = null) {
  const history = getHistory(walletAddress);
  
  if (!history || history.length === 0) {
    return {
      totalPurifications: 0,
      totalObjectsCleaned: 0,
      totalPurityEarned: 0,
      totalSuiReclaimed: 0,
      averagePurityPerPurification: 0,
      mostProductiveDay: null,
      currentStreak: 0,
      longestStreak: 0,
      rank: null,
      percentile: null,
      efficiency: 0,
      favoriteCategory: null,
    };
  }
  
  // Basic totals
  const totalPurifications = history.length;
  const totalObjectsCleaned = history.reduce((sum, p) => sum + p.objectCount, 0);
  const totalPurityEarned = history.reduce((sum, p) => sum + p.purityEarned, 0);
  const totalSuiReclaimed = history.reduce((sum, p) => sum + p.suiReclaimed, 0);
  
  // Averages
  const averagePurityPerPurification = totalPurifications > 0 
    ? Math.round(totalPurityEarned / totalPurifications) 
    : 0;
  
  // Most productive day
  const dayStats = {};
  history.forEach(p => {
    const day = new Date(p.timestamp).toISOString().split('T')[0];
    if (!dayStats[day]) {
      dayStats[day] = { date: day, purity: 0, objects: 0 };
    }
    dayStats[day].purity += p.purityEarned;
    dayStats[day].objects += p.objectCount;
  });
  
  const mostProductiveDay = Object.values(dayStats).reduce((best, day) => 
    !best || day.purity > best.purity ? day : best
  , null);
  
  // Streak calculation
  const { currentStreak, longestStreak } = calculateStreaks(history);
  
  // Efficiency (objects cleaned per purification)
  const efficiency = totalPurifications > 0 
    ? (totalObjectsCleaned / totalPurifications).toFixed(1)
    : 0;
  
  // Favorite category
  const categoryTotals = { TOXIC: 0, SPAM: 0, DUST: 0 };
  history.forEach(p => {
    if (p.categoryBreakdown) {
      categoryTotals.TOXIC += p.categoryBreakdown.TOXIC || 0;
      categoryTotals.SPAM += p.categoryBreakdown.SPAM || 0;
      categoryTotals.DUST += p.categoryBreakdown.DUST || 0;
    }
  });
  const favoriteCategory = Object.keys(categoryTotals).reduce((a, b) => 
    categoryTotals[a] > categoryTotals[b] ? a : b
  );
  
  return {
    totalPurifications,
    totalObjectsCleaned,
    totalPurityEarned,
    totalSuiReclaimed,
    averagePurityPerPurification,
    mostProductiveDay,
    currentStreak,
    longestStreak,
    efficiency: parseFloat(efficiency),
    favoriteCategory,
    categoryBreakdown: categoryTotals,
  };
}

/**
 * Calculate current and longest streak
 */
function calculateStreaks(history) {
  if (!history || history.length === 0) {
    return { currentStreak: 0, longestStreak: 0 };
  }
  
  // Get unique days with purifications
  const days = [...new Set(history.map(p => 
    new Date(p.timestamp).toISOString().split('T')[0]
  ))].sort();
  
  if (days.length === 0) return { currentStreak: 0, longestStreak: 0 };
  
  let currentStreak = 0;
  let longestStreak = 0;
  let tempStreak = 1;
  
  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  
  // Check if last purification was today or yesterday (current streak alive)
  const lastDay = days[days.length - 1];
  if (lastDay === today || lastDay === yesterday) {
    currentStreak = 1;
    
    // Count backwards
    for (let i = days.length - 2; i >= 0; i--) {
      const currentDate = new Date(days[i]);
      const nextDate = new Date(days[i + 1]);
      const dayDiff = Math.floor((nextDate - currentDate) / (24 * 60 * 60 * 1000));
      
      if (dayDiff === 1) {
        currentStreak++;
      } else {
        break;
      }
    }
  }
  
  // Calculate longest streak
  for (let i = 1; i < days.length; i++) {
    const prevDate = new Date(days[i - 1]);
    const currDate = new Date(days[i]);
    const dayDiff = Math.floor((currDate - prevDate) / (24 * 60 * 60 * 1000));
    
    if (dayDiff === 1) {
      tempStreak++;
      longestStreak = Math.max(longestStreak, tempStreak);
    } else {
      tempStreak = 1;
    }
  }
  
  longestStreak = Math.max(longestStreak, currentStreak);
  
  return { currentStreak, longestStreak };
}

// ═══════════════════════════════════════════════════════════════════════════
// PROTOCOL-WIDE STATS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get protocol-wide statistics
 * In production, this would query a blockchain indexer or backend API
 */
export function getProtocolStats() {
  // In demo mode or development, return simulated data
  return DEMO_PROTOCOL_STATS;
}

/**
 * Get leaderboard rankings
 */
export function getLeaderboard(limit = 10) {
  const stats = getProtocolStats();
  return stats.topPurifiers.slice(0, limit);
}

/**
 * Get user's rank on leaderboard
 */
export function getUserRank(userPurity) {
  const leaderboard = getProtocolStats().topPurifiers;
  
  // Find where user would rank
  let rank = 1;
  for (const purifier of leaderboard) {
    if (userPurity >= purifier.totalPurity) {
      return rank;
    }
    rank++;
  }
  
  return rank;
}

/**
 * Get user's percentile (top X%)
 */
export function getUserPercentile(userPurity) {
  const stats = getProtocolStats();
  const totalUsers = stats.activePurifiers;
  const rank = getUserRank(userPurity);
  
  const percentile = ((totalUsers - rank + 1) / totalUsers * 100).toFixed(1);
  return parseFloat(percentile);
}

/**
 * Get daily activity chart data
 */
export function getDailyActivity(days = 30) {
  const stats = getProtocolStats();
  return stats.dailyActivity.slice(-days);
}

/**
 * Get category breakdown for pie chart
 */
export function getCategoryBreakdown() {
  const stats = getProtocolStats();
  return stats.categoriesBreakdown;
}

// ═══════════════════════════════════════════════════════════════════════════
// MILESTONES & ACHIEVEMENTS
// ═══════════════════════════════════════════════════════════════════════════

export const MILESTONES = [
  { id: 'first_purification', name: 'First Steps', description: 'Complete your first purification', threshold: 1, icon: '🌱' },
  { id: 'purifier_10', name: 'Getting Started', description: 'Complete 10 purifications', threshold: 10, icon: '⚡' },
  { id: 'purifier_50', name: 'Dedicated Cleaner', description: 'Complete 50 purifications', threshold: 50, icon: '🔥' },
  { id: 'purifier_100', name: 'Cleanup Veteran', description: 'Complete 100 purifications', threshold: 100, icon: '💎' },
  { id: 'purity_1000', name: 'Purity Seeker', description: 'Earn 1,000 purity points', threshold: 1000, icon: '✨' },
  { id: 'purity_10000', name: 'Purity Master', description: 'Earn 10,000 purity points', threshold: 10000, icon: '🌟' },
  { id: 'streak_7', name: 'Week Warrior', description: 'Maintain a 7-day streak', threshold: 7, icon: '🔥' },
  { id: 'streak_30', name: 'Monthly Champion', description: 'Maintain a 30-day streak', threshold: 30, icon: '👑' },
];

/**
 * Check which milestones user has achieved
 */
export function getAchievedMilestones(walletAddress = null) {
  const personal = getPersonalStats(walletAddress);
  
  return MILESTONES.map(milestone => {
    let achieved = false;
    let progress = 0;
    
    if (milestone.id.startsWith('purifier_')) {
      progress = personal.totalPurifications;
      achieved = progress >= milestone.threshold;
    } else if (milestone.id.startsWith('purity_')) {
      progress = personal.totalPurityEarned;
      achieved = progress >= milestone.threshold;
    } else if (milestone.id.startsWith('streak_')) {
      progress = personal.longestStreak;
      achieved = progress >= milestone.threshold;
    } else if (milestone.id === 'first_purification') {
      progress = personal.totalPurifications > 0 ? 1 : 0;
      achieved = personal.totalPurifications > 0;
    }
    
    return {
      ...milestone,
      achieved,
      progress,
      percentage: Math.min(100, (progress / milestone.threshold) * 100),
    };
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT ALL FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

export default {
  getPersonalStats,
  getProtocolStats,
  getLeaderboard,
  getUserRank,
  getUserPercentile,
  getDailyActivity,
  getCategoryBreakdown,
  getAchievedMilestones,
  MILESTONES,
};

// ═══════════════════════════════════════════════════════════════════════════
// BURN TRACKING SYSTEM - TASK 1 (Deflationary Tokenomics)
// Tracks 1% of all rewards that are burned permanently
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Track a burn event
 * @param {number} amount - Amount of $DISTIL burned
 */
export function trackBurn(amount) {
  // Update total burned
  const currentBurned = parseFloat(localStorage.getItem('total_distil_burned') || '0');
  const newTotal = currentBurned + amount;
  localStorage.setItem('total_distil_burned', newTotal.toString());
  
  // Track period burns (weekly/monthly)
  trackPeriodBurn('weekly', amount);
  trackPeriodBurn('monthly', amount);
  
  // Add to burn history
  const history = JSON.parse(localStorage.getItem('burn_history') || '[]');
  history.push({
    amount,
    timestamp: Date.now(),
    totalBurned: newTotal
  });
  
  // Keep last 100 burns
  if (history.length > 100) history.shift();
  localStorage.setItem('burn_history', JSON.stringify(history));
}

/**
 * Get total amount of $DISTIL burned
 */
export function getTotalBurned() {
  return parseFloat(localStorage.getItem('total_distil_burned') || '0');
}

/**
 * Get percentage of total supply that's been burned
 */
export function getBurnPercentage() {
  const totalBurned = getTotalBurned();
  const TOTAL_SUPPLY = 100_000_000; // 100M total supply
  return (totalBurned / TOTAL_SUPPLY) * 100;
}

/**
 * Get weekly burn amount
 */
export function getWeeklyBurned() {
  const data = JSON.parse(localStorage.getItem('distil_burned_weekly') || '{"total": 0}');
  return data.total || 0;
}

/**
 * Get monthly burn amount
 */
export function getMonthlyBurned() {
  const data = JSON.parse(localStorage.getItem('distil_burned_monthly') || '{"total": 0}');
  return data.total || 0;
}

/**
 * Get burn history (for charts/graphs)
 */
export function getBurnHistory() {
  return JSON.parse(localStorage.getItem('burn_history') || '[]');
}

/**
 * Track period-based burns (weekly/monthly)
 * @private
 */
function trackPeriodBurn(period, amount) {
  const key = `distil_burned_${period}`;
  const data = JSON.parse(localStorage.getItem(key) || '{"total": 0, "timestamp": null}');
  
  const now = Date.now();
  const periodLength = period === 'weekly' ? 7 * 24 * 60 * 60 * 1000 : 30 * 24 * 60 * 60 * 1000;
  
  // Reset if period has elapsed
  if (data.timestamp && (now - data.timestamp > periodLength)) {
    data.total = 0;
    data.timestamp = now;
  } else if (!data.timestamp) {
    data.timestamp = now;
  }
  
  data.total += amount;
  localStorage.setItem(key, JSON.stringify(data));
}

/**
 * Get burn stats summary
 */
export function getBurnStats() {
  return {
    totalBurned: getTotalBurned(),
    burnPercentage: getBurnPercentage(),
    weeklyBurned: getWeeklyBurned(),
    monthlyBurned: getMonthlyBurned(),
    burnHistory: getBurnHistory()
  };
}
/**
 * Shows how much gas users save by batching deletions vs manual one-by-one
 */
export function getGasStats(walletAddress = null) {
  const history = getHistory(walletAddress);
  
  if (!history || history.length === 0) {
    return {
      totalGasUsed: 0,
      totalObjectsCleaned: 0,
      averageGasPerPurification: 0,
      averageGasPerObject: 0,
      estimatedManualGas: 0,
      totalGasSaved: 0,
      gasSavingsPercentage: 0,
      gasSavedInUSD: 0,
      efficiencyScore: 0,
    };
  }
  
  // Calculate actual gas used
  const totalGasUsed = history.reduce((sum, p) => sum + (p.gasUsed || 0), 0);
  const totalObjectsCleaned = history.reduce((sum, p) => sum + p.objectCount, 0);
  const totalPurifications = history.length;
  
  // Average gas metrics
  const averageGasPerPurification = totalPurifications > 0 
    ? totalGasUsed / totalPurifications 
    : 0;
  const averageGasPerObject = totalObjectsCleaned > 0 
    ? totalGasUsed / totalObjectsCleaned 
    : 0;
  
  // Estimate manual gas (if user deleted each object individually)
  // Typical single object deletion: ~0.0015 SUI gas
  const estimatedManualGas = totalObjectsCleaned * 0.0015;
  
  // Calculate savings
  const totalGasSaved = estimatedManualGas - totalGasUsed;
  const gasSavingsPercentage = estimatedManualGas > 0
    ? ((totalGasSaved / estimatedManualGas) * 100)
    : 0;
  
  // Convert to USD (assuming $2.50 per SUI - update with real price)
  const SUI_PRICE_USD = 2.50;
  const gasSavedInUSD = totalGasSaved * SUI_PRICE_USD;
  
  // Efficiency score (objects per gas unit)
  const efficiencyScore = totalGasUsed > 0 
    ? totalObjectsCleaned / totalGasUsed 
    : 0;
  
  return {
    totalGasUsed: Number(totalGasUsed.toFixed(4)),
    totalObjectsCleaned,
    averageGasPerPurification: Number(averageGasPerPurification.toFixed(4)),
    averageGasPerObject: Number(averageGasPerObject.toFixed(6)),
    estimatedManualGas: Number(estimatedManualGas.toFixed(4)),
    totalGasSaved: Number(totalGasSaved.toFixed(4)),
    gasSavingsPercentage: Number(gasSavingsPercentage.toFixed(1)),
    gasSavedInUSD: Number(gasSavedInUSD.toFixed(2)),
    efficiencyScore: Number(efficiencyScore.toFixed(0)),
  };
}

/**
 * Get gas usage over time for charting
 * Returns daily gas usage and savings
 */
export function getGasUsageOverTime(days = 30, walletAddress = null) {
  const history = getHistory(walletAddress);
  const now = Date.now();
  const oneDay = 24 * 60 * 60 * 1000;
  
  const dailyData = [];
  
  for (let i = days - 1; i >= 0; i--) {
    const dayStart = now - (i * oneDay);
    const dayEnd = dayStart + oneDay;
    
    const dayPurifications = history.filter(p => 
      p.timestamp >= dayStart && p.timestamp < dayEnd
    );
    
    const gasUsed = dayPurifications.reduce((sum, p) => sum + (p.gasUsed || 0), 0);
    const objectsCleaned = dayPurifications.reduce((sum, p) => sum + p.objectCount, 0);
    const estimatedManual = objectsCleaned * 0.0015;
    const saved = estimatedManual - gasUsed;
    
    dailyData.push({
      date: new Date(dayStart).toISOString().split('T')[0],
      gasUsed: Number(gasUsed.toFixed(4)),
      objectsCleaned,
      gasSaved: Number(saved.toFixed(4)),
      purifications: dayPurifications.length,
    });
  }
  
  return dailyData;
}

/**
 * Get best and worst gas efficiency purifications
 */
export function getGasEfficiencyExtremes(walletAddress = null) {
  const history = getHistory(walletAddress);
  
  if (!history || history.length === 0) {
    return { mostEfficient: null, leastEfficient: null };
  }
  
  // Calculate efficiency for each purification (objects per gas)
  const withEfficiency = history
    .filter(p => p.gasUsed > 0)
    .map(p => ({
      ...p,
      efficiency: p.objectCount / p.gasUsed,
    }));
  
  if (withEfficiency.length === 0) {
    return { mostEfficient: null, leastEfficient: null };
  }
  
  withEfficiency.sort((a, b) => b.efficiency - a.efficiency);
  
  return {
    mostEfficient: withEfficiency[0],
    leastEfficient: withEfficiency[withEfficiency.length - 1],
  };
}

/**
 * Calculate potential future savings
 * Estimates gas savings if user continues at current rate
 */
export function getProjectedSavings(walletAddress = null) {
  const stats = getGasStats(walletAddress);
  const history = getHistory(walletAddress);
  
  if (!history || history.length === 0) {
    return {
      nextMonth: 0,
      nextYear: 0,
      next100Purifications: 0,
    };
  }
  
  // Calculate average purifications per day
  const oldestPurification = history[history.length - 1];
  const daysActive = Math.max(1, (Date.now() - oldestPurification.timestamp) / (24 * 60 * 60 * 1000));
  const purificationsPerDay = history.length / daysActive;
  
  // Calculate average objects per purification
  const avgObjectsPerPurification = stats.totalObjectsCleaned / history.length;
  
  // Calculate average gas saved per purification
  const avgGasSavedPerPurification = stats.totalGasSaved / history.length;
  
  return {
    nextMonth: Number((purificationsPerDay * 30 * avgGasSavedPerPurification).toFixed(4)),
    nextYear: Number((purificationsPerDay * 365 * avgGasSavedPerPurification).toFixed(2)),
    next100Purifications: Number((100 * avgGasSavedPerPurification).toFixed(4)),
  };
}

/**
 * Get gas comparison data for visualization
 */
export function getGasComparison() {
  const stats = getGasStats();
  
  return {
    withDistil: {
      label: 'With Distil (Batch)',
      value: stats.totalGasUsed,
      color: '#22d3ee', // cyan
    },
    withoutDistil: {
      label: 'Manual (One-by-One)',
      value: stats.estimatedManualGas,
      color: '#ef4444', // red
    },
    savings: {
      absolute: stats.totalGasSaved,
      percentage: stats.gasSavingsPercentage,
      usd: stats.gasSavedInUSD,
    },
  };
}
